import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useSellerProducts } from "@/hooks/use-sellers";
import { formatCurrency } from "@/lib/format";
import type { Product } from "@/types";

/**
 * Escolha dos produtos com frete grátis de um estabelecimento, para o cupom de
 * frete grátis por produto. O mesmo passo do app do vendedor: a seleção só
 * vale ao confirmar, e cancelar devolve o cupom como estava.
 */
export function FreeShippingProductsDialog({
  open,
  onOpenChange,
  sellerId,
  selectedIds,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sellerId: string;
  selectedIds: string[];
  onConfirm: (productIds: string[]) => void;
}) {
  const products = useSellerProducts(open ? sellerId : undefined);
  const [draft, setDraft] = useState<string[]>(selectedIds);
  const [search, setSearch] = useState("");

  // Recomeça da seleção do cupom toda vez que o diálogo abre.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setDraft(selectedIds);
      setSearch("");
    }
  }

  const sections = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = (products.data ?? []).filter(
      (p) => !term || (p.name ?? "").toLowerCase().includes(term),
    );
    return [
      { title: "Pronta-entrega", rows: rows.filter((p) => !p.isReserveItem) },
      { title: "Encomenda", rows: rows.filter((p) => p.isReserveItem) },
    ].filter((section) => section.rows.length > 0);
  }, [products.data, search]);

  function toggle(product: Product) {
    setDraft((prev) =>
      prev.includes(product.id) ? prev.filter((id) => id !== product.id) : [...prev, product.id],
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Produtos com frete grátis</DialogTitle>
          <DialogDescription>
            Quem comprar um dos produtos marcados não paga a entrega.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar produto"
            className="pl-9"
            aria-label="Buscar produto"
          />
        </div>

        <div className="max-h-[50dvh] space-y-3 overflow-y-auto">
          {products.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
            </div>
          ) : sections.length === 0 ? (
            <p className="text-small text-muted-foreground">
              {(products.data ?? []).length === 0
                ? "Este estabelecimento ainda não tem produtos no cardápio."
                : "Nenhum produto encontrado."}
            </p>
          ) : (
            sections.map((section) => (
              <div key={section.title} className="space-y-2">
                <p className="text-caption font-semibold text-muted-foreground">{section.title}</p>
                {section.rows.map((product) => (
                  <label
                    key={product.id}
                    className="flex cursor-pointer items-center gap-3 rounded-md border border-border p-2.5 has-[:checked]:border-primary has-[:checked]:bg-primary/5"
                  >
                    <input
                      type="checkbox"
                      className="size-4 shrink-0 accent-primary"
                      checked={draft.includes(product.id)}
                      onChange={() => toggle(product)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-small font-semibold">
                        {product.name || product.id}
                      </span>
                      {product.isAvailable === false ? (
                        <span className="block text-caption text-muted-foreground">
                          Indisponível no momento
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-caption tabular-nums text-muted-foreground">
                      {formatCurrency(product.price)}
                    </span>
                  </label>
                ))}
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" onClick={() => onConfirm(draft)}>
            {draft.length > 0 ? `Confirmar (${draft.length})` : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
